<template>
  <v-dialog v-model="dialogModel" max-width="800" persistent>
    <v-card>
      <v-card-title>
        {{ title }}
      </v-card-title>
      <v-card-text>
        <v-form ref="form" @submit.prevent="save">
          <v-container>
            <v-row>
              <!-- Nueva versión de un producto existente (otro código de barras del mismo producto) -->
              <v-col v-if="mode === 'add'" cols="12" class="pb-0">
                <v-checkbox
                  v-model="isNewVersion"
                  label="Nueva versión de un producto existente"
                  hint="El código de barras se agrega al producto seleccionado, sin crear un producto nuevo"
                  persistent-hint
                  color="primary"
                  density="compact"
                ></v-checkbox>
              </v-col>
              <v-col v-if="isNewVersion" cols="12">
                <v-autocomplete
                  v-model="versionOfProductId"
                  :items="versionCandidates"
                  item-title="label"
                  item-value="id"
                  label="Producto existente *"
                  placeholder="Escribe el código o nombre del producto"
                  prepend-inner-icon="mdi-magnify"
                  :rules="[v => !!v || 'Seleccione el producto del que es nueva versión']"
                  no-data-text="Sin coincidencias"
                  auto-select-first
                  clearable
                >
                  <template v-slot:item="{ props: itemProps, item }">
                    <v-list-item v-bind="itemProps" :title="item.raw.sku" :subtitle="item.raw.subtitle"></v-list-item>
                  </template>
                </v-autocomplete>
              </v-col>

              <!-- Información básica -->
              <v-col v-if="!isNewVersion" cols="12" md="6">
                <v-text-field
                  v-model="productData.sku"
                  label="SKU *"
                  :rules="[v => !!v || 'SKU es requerido']"
                  :disabled="mode === 'edit'"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="productData.barcode"
                  label="Código de Barras *"
                  :rules="[v => !!v || 'Código de Barras es requerido']"
                ></v-text-field>
              </v-col>
              <template v-if="!isNewVersion">
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="productData.codigo_fabrica"
                  label="Código de Fábrica"
                  hint="Código del fabricante (opcional)"
                  persistent-hint
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="productData.name"
                  label="Nombre *"
                  :rules="[v => !!v || 'Nombre es requerido']"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="productData.description"
                  label="Descripción"
                  rows="3"
                ></v-textarea>
              </v-col>

              <!-- Categorización -->
              <v-col cols="12" md="6">
                <div class="d-flex align-center">
                  <v-select
                    v-model="productData.category"
                    :items="categories"
                    label="Categoría *"
                    item-title="title"
                    item-value="value"
                    :rules="[v => !!v || 'Categoría es requerida']"
                  ></v-select>
                  <v-btn
                    icon="mdi-plus"
                    color="primary"
                    class="ms-2"
                    @click="showNewCategoryDialog = true"
                  ></v-btn>
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="productData.abc_category"
                  :items="abcCategories"
                  label="Categoría ABC"
                ></v-select>
              </v-col>

              <!-- Ubicación -->
              <v-col cols="12" md="6">
                <v-select
                  v-model="productData.warehouse_id"
                  :items="warehouses"
                  label="Bodega *"
                  item-title="name"
                  item-value="id"
                  :rules="[v => !!v || 'Bodega es requerida']"
                  @update:model-value="loadZones"
                ></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="productData.zone_id"
                  :items="zones"
                  label="Zona *"
                  item-title="name"
                  item-value="id"
                  :rules="[v => !!v || 'Zona es requerida']"
                  :disabled="!productData.warehouse_id"
                  @update:model-value="loadZoneLocations"
                ></v-select>
              </v-col>
              <v-col cols="12">
                <div class="d-flex align-center">
                  <v-select
                    v-model="productData.location_id"
                    :items="availableLocations"
                    label="Ubicación Específica *"
                    item-title="display_text"
                    item-value="id"
                    :rules="[v => !!v || 'Ubicación es requerida']"
                    :disabled="!productData.zone_id"
                    :loading="loadingLocations"
                    clearable
                  >
                    <template v-slot:prepend>
                      <v-icon>mdi-map-marker</v-icon>
                    </template>
                    <template v-slot:item="{ props, item }">
                      <v-list-item v-bind="props">
                        <template v-slot:title>
                          <div class="d-flex justify-space-between align-center">
                            <div>
                              <strong>{{ item.raw.code }}</strong>
                              <div class="text-caption text-grey">{{ item.raw.description || 'Sin descripción' }}</div>
                            </div>
                            <div class="text-caption">
                              <v-chip size="x-small" :color="getLocationOccupancyColor(item.raw)">
                                {{ Math.round(getLocationOccupancy(item.raw)) }}% ocupado
                              </v-chip>
                            </div>
                          </div>
                        </template>
                        <template v-slot:subtitle>
                          <div class="text-caption">
                            <span v-if="item.raw.barcode">Código: {{ item.raw.barcode }}</span>
                            <span v-if="item.raw.aisle || item.raw.rack || item.raw.level">
                              | Coordenadas: 
                              <span v-if="item.raw.aisle">I:{{ item.raw.aisle }}</span>
                              <span v-if="item.raw.rack"> R:{{ item.raw.rack }}</span>
                              <span v-if="item.raw.level"> N:{{ item.raw.level }}</span>
                            </span>
                          </div>
                        </template>
                      </v-list-item>
                    </template>
                  </v-select>
                  <v-btn
                    icon="mdi-plus"
                    color="primary"
                    class="ms-2"
                    @click="showNewLocationDialog = true"
                    :disabled="!productData.zone_id"
                    :title="!productData.zone_id ? 'Seleccione una zona primero' : 'Crear nueva ubicación'"
                  ></v-btn>
                </div>
                <div v-if="selectedLocationInfo" class="mt-2">
                  <v-card variant="outlined" class="pa-2">
                    <div class="text-caption">
                      <strong>Ubicación seleccionada:</strong> {{ selectedLocationInfo.code }}
                      <br>
                      <span v-if="selectedLocationInfo.description">{{ selectedLocationInfo.description }}</span>
                      <br>
                      <span v-if="selectedLocationInfo.barcode">Código de barras: {{ selectedLocationInfo.barcode }}</span>
                      <br>
                      Capacidad: {{ selectedLocationInfo.current_usage || 0 }}/{{ selectedLocationInfo.capacity || 0 }}
                      ({{ Math.round(getLocationOccupancy(selectedLocationInfo)) }}% ocupado)
                    </div>
                  </v-card>
                </div>
              </v-col>

              <!-- Stock -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="productData.current_stock"
                  type="number"
                  label="Stock Actual"
                  :rules="[v => v >= 0 || 'Stock no puede ser negativo']"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="productData.min_stock"
                  type="number"
                  label="Stock Mínimo"
                  :rules="[v => v >= 0 || 'Stock mínimo no puede ser negativo']"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="productData.max_stock"
                  type="number"
                  label="Stock Máximo"
                  :rules="[
                    v => v >= 0 || 'Stock máximo no puede ser negativo',
                    v => !productData.min_stock || !v || v > productData.min_stock || 'Stock máximo debe ser mayor al stock mínimo'
                  ]"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="productData.cantidad_x_caja"
                  type="number"
                  label="Cantidad por Caja"
                  min="1"
                  hint="Unidades por caja/paquete (por defecto 1)"
                  persistent-hint
                  :rules="[v => !v || v >= 1 || 'Debe ser al menos 1']"
                ></v-text-field>
              </v-col>

              <!-- Dimensiones y peso -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="productData.weight"
                  type="number"
                  label="Peso (kg)"
                  :rules="[v => !v || v > 0 || 'Si se especifica, el peso debe ser mayor a 0']"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="productData.length"
                  type="number"
                  :label="locationConfig.volumetricCapacity.enabled ? 'Largo (cm) *' : 'Largo (cm)'"
                  :rules="locationConfig.volumetricCapacity.enabled ? 
                    [v => !!v && v > 0 || 'Largo es requerido y debe ser mayor a 0'] : 
                    [v => !v || v > 0 || 'Si se especifica, el largo debe ser mayor a 0']"
                  :hint="locationConfig.volumetricCapacity.enabled ? 'Campo obligatorio para capacidad volumétrica' : 'Campo opcional'"
                  persistent-hint
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="productData.width"
                  type="number"
                  :label="locationConfig.volumetricCapacity.enabled ? 'Ancho (cm) *' : 'Ancho (cm)'"
                  :rules="locationConfig.volumetricCapacity.enabled ? 
                    [v => !!v && v > 0 || 'Ancho es requerido y debe ser mayor a 0'] : 
                    [v => !v || v > 0 || 'Si se especifica, el ancho debe ser mayor a 0']"
                  :hint="locationConfig.volumetricCapacity.enabled ? 'Campo obligatorio para capacidad volumétrica' : 'Campo opcional'"
                  persistent-hint
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="productData.height"
                  type="number"
                  :label="locationConfig.volumetricCapacity.enabled ? 'Alto (cm) *' : 'Alto (cm)'"
                  :rules="locationConfig.volumetricCapacity.enabled ? 
                    [v => !!v && v > 0 || 'Alto es requerido y debe ser mayor a 0'] : 
                    [v => !v || v > 0 || 'Si se especifica, el alto debe ser mayor a 0']"
                  :hint="locationConfig.volumetricCapacity.enabled ? 'Campo obligatorio para capacidad volumétrica' : 'Campo opcional'"
                  persistent-hint
                ></v-text-field>
              </v-col>

              <!-- Estado -->
              <v-col cols="12" md="6">
                <v-select
                  v-model="productData.status"
                  :items="statusOptions"
                  label="Estado *"
                  :rules="[v => !!v || 'Estado es requerido']"
                ></v-select>
              </v-col>
              </template>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <div class="text-caption text-grey ms-3">
          * Campos obligatorios
        </div>
        <v-spacer></v-spacer>
        <v-btn
          color="grey"
          variant="text"
          @click="close"
        >
          Cancelar
        </v-btn>
        <v-btn
          color="primary"
          @click="save"
          :loading="saving"
        >
          Guardar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Diálogo para nueva categoría -->
  <v-dialog v-model="showNewCategoryDialog" max-width="400">
    <v-card>
      <v-card-title>Nueva Categoría</v-card-title>
      <v-card-text>
        <v-text-field
          v-model="newCategory"
          label="Nombre de la categoría"
          :rules="[v => !!v || 'El nombre es requerido']"
        ></v-text-field>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="grey"
          variant="text"
          @click="showNewCategoryDialog = false"
        >
          Cancelar
        </v-btn>
        <v-btn
          color="primary"
          @click="createCategory"
          :loading="creatingCategory"
        >
          Crear
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Diálogo para nueva ubicación -->
  <v-dialog v-model="showNewLocationDialog" max-width="600">
    <v-card>
      <v-card-title>Nueva Ubicación</v-card-title>
      <v-card-text>
        <v-form ref="locationForm">
          <v-text-field
            v-model="newLocationData.code"
            label="Código de Ubicación"
            :rules="[v => !!v || 'El código es requerido']"
            hint="Ej: A010101, B020202"
            persistent-hint
          ></v-text-field>
          
          <v-textarea
            v-model="newLocationData.description"
            label="Descripción"
            rows="2"
          ></v-textarea>
          
          <v-select
            v-model="newLocationData.location_type"
            :items="[
              { title: 'Almacenamiento', value: 'storage' },
              { title: 'Picking', value: 'picking' },
              { title: 'Recepción', value: 'receiving' },
              { title: 'Despacho', value: 'shipping' },
              { title: 'Control de Calidad', value: 'quality_control' },
              { title: 'Devoluciones', value: 'returns' }
            ]"
            label="Tipo de Ubicación"
            :rules="[v => !!v || 'El tipo de ubicación es requerido']"
          ></v-select>
          
          <v-row>
            <v-col cols="12" md="3">
              <v-text-field
                v-model="newLocationData.aisle"
                label="Isla"
                placeholder="Ej: A01"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                v-model="newLocationData.rack"
                label="Rack"
                placeholder="Ej: R01"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                v-model="newLocationData.level"
                label="Nivel"
                placeholder="Ej: L01"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                v-model="newLocationData.position"
                label="Posición"
                placeholder="Ej: P01"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model.number="newLocationData.weight"
                type="number"
                label="Peso (para cálculo de ruta)"
                min="1"
                step="1"
                :rules="[v => v > 0 || 'El peso debe ser mayor a 0']"
                hint="Mayor peso indica mayor distancia o dificultad de acceso"
                persistent-hint
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model.number="newLocationData.capacity"
                type="number"
                label="Capacidad máxima"
                min="1"
                step="1"
                :rules="[v => v > 0 || 'La capacidad debe ser mayor a 0']"
                hint="Cantidad máxima de productos que admite"
                persistent-hint
              ></v-text-field>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="grey"
          variant="text"
          @click="showNewLocationDialog = false"
        >
          Cancelar
        </v-btn>
        <v-btn
          color="primary"
          @click="createLocation"
          :loading="creatingLocation"
        >
          Crear Ubicación
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useStore } from 'vuex'
import api from '@/api'
import { productBarcodes } from '@/services/productBarcodes'

// Props
const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true
  },
  product: {
    type: Object,
    default: () => null
  },
  mode: {
    type: String,
    default: 'add',
    validator: value => ['add', 'edit'].includes(value)
  }
})

// Emits
const emit = defineEmits(['update:modelValue', 'save', 'version-added'])

// Store
const store = useStore()

// Referencias
const form = ref(null)
const locationForm = ref(null)

// Estado
const saving = ref(false)
const creatingCategory = ref(false)
const creatingLocation = ref(false)
const loadingLocations = ref(false)
const showNewCategoryDialog = ref(false)
const showNewLocationDialog = ref(false)
const newCategory = ref('')

// Nueva versión de un producto existente
const isNewVersion = ref(false)
const versionOfProductId = ref(null)

// Datos de nueva ubicación
const newLocationData = ref({
  code: '',
  description: '',
  location_type: 'storage',
  aisle: '',
  rack: '',
  level: '',
  position: '',
  weight: 1,
  capacity: 1,
  zone_id: null
})

// Datos del producto
const productData = ref({
  sku: '',
  barcode: '',
  codigo_fabrica: '',
  name: '',
  description: '',
  category: '',
  abc_category: '',
  warehouse_id: null,
  zone_id: null,
  location_id: null,
  current_stock: 0,
  min_stock: 0,
  max_stock: 0,
  cantidad_x_caja: 1,
  weight: 0,
  length: 0,
  width: 0,
  height: 0,
  status: 'Activo'
})

// Estado para ubicaciones
const zones = ref([])
const availableLocations = ref([])

// Configuración de ubicaciones (para capacidad volumétrica)
const locationConfig = ref({
  volumetricCapacity: {
    enabled: false
  }
})

// Computed
const dialogModel = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const title = computed(() => {
  return props.mode === 'edit' ? 'Editar Producto' : 'Nuevo Producto'
})

const warehouses = computed(() => store.getters['warehouses/allWarehouses'] || [])

const versionCandidates = computed(() => {
  const products = store.getters['products/allProducts'] || []
  return products.map(p => ({
    id: p.id,
    sku: p.sku,
    // label se usa para filtrar mientras se escribe (código y nombre)
    label: `${p.sku} - ${p.name}`,
    subtitle: `${p.name} · ${productBarcodes(p).length} código(s) de barra`
  }))
})

const categories = computed(() => {
  const cats = store.getters['categories/allCategories'] || []
  return cats.map(cat => ({
    title: cat.name,
    value: cat.name
  }))
})

const abcCategories = computed(() => [
  { title: 'A - Alta rotación', value: 'A' },
  { title: 'B - Media rotación', value: 'B' },
  { title: 'C - Baja rotación', value: 'C' }
])

const statusOptions = computed(() => [
  { title: 'Activo', value: 'Activo' },
  { title: 'Inactivo', value: 'Inactivo' },
  { title: 'Descontinuado', value: 'Descontinuado' }
])

const selectedLocationInfo = computed(() => {
  if (!productData.value.location_id) return null
  return availableLocations.value.find(loc => loc.id === productData.value.location_id)
})

// Watchers
watch(() => props.modelValue, (open) => {
  if (open) {
    isNewVersion.value = false
    versionOfProductId.value = null
  }
})

watch(() => props.product, (newProduct) => {
  if (newProduct) {
    productData.value = { ...newProduct }
    if (newProduct.warehouse_id) {
      loadZones()
    }
    if (newProduct.zone_id) {
      loadZoneLocations()
    }
  } else {
    resetForm()
  }
}, { immediate: true })

// Métodos
const resetForm = () => {
  productData.value = {
    sku: '',
    barcode: '',
    name: '',
    description: '',
    category: '',
    abc_category: '',
    warehouse_id: null,
    zone_id: null,
    location_id: null,
    current_stock: 0,
    min_stock: 0,
    max_stock: 0,
    weight: 0,
    length: 0,
    width: 0,
    height: 0,
    status: 'Activo'
  }
  zones.value = []
  availableLocations.value = []
}

const loadZones = async () => {
  if (!productData.value.warehouse_id) {
    zones.value = []
    productData.value.zone_id = null
    productData.value.location_id = null
    availableLocations.value = []
    return
  }

  try {
    const response = await api.getWarehouseZones(productData.value.warehouse_id)
    zones.value = response.data
  } catch (error) {
    console.error('Error loading zones:', error)
    store.dispatch('showError', 'Error al cargar las zonas')
  }
}

const loadZoneLocations = async () => {
  if (!productData.value.zone_id) {
    availableLocations.value = []
    productData.value.location_id = null
    return
  }

  try {
    loadingLocations.value = true
    const response = await api.getLocationsByZone(productData.value.zone_id)
    
    // Formatear ubicaciones para el selector
    availableLocations.value = response.data.map(location => ({
      ...location,
      display_text: `${location.code} - ${location.description || 'Sin descripción'}`,
      current_usage: location.current_usage || 0
    }))
  } catch (error) {
    console.error('Error loading zone locations:', error)
    store.dispatch('showError', 'Error al cargar las ubicaciones')
  } finally {
    loadingLocations.value = false
  }
}

const getLocationOccupancy = (location) => {
  if (!location.capacity || location.capacity === 0) return 0
  return ((location.current_usage || 0) / location.capacity) * 100
}

const getLocationOccupancyColor = (location) => {
  const occupancy = getLocationOccupancy(location)
  if (occupancy < 50) return 'success'
  if (occupancy < 80) return 'warning'
  return 'error'
}

const createCategory = async () => {
  if (!newCategory.value.trim()) return

  try {
    creatingCategory.value = true
    await store.dispatch('categories/createCategory', {
      name: newCategory.value.trim()
    })
    
    // Seleccionar la nueva categoría
    productData.value.category = newCategory.value.trim()
    
    // Limpiar y cerrar modal
    newCategory.value = ''
    showNewCategoryDialog.value = false
    
    store.dispatch('showSuccess', 'Categoría creada exitosamente')
  } catch (error) {
    console.error('Error creating category:', error)
    store.dispatch('showError', 'Error al crear la categoría')
  } finally {
    creatingCategory.value = false
  }
}

const createLocation = async () => {
  try {
    const { valid } = await locationForm.value.validate()
    if (!valid) return

    creatingLocation.value = true
    
    const locationPayload = {
      ...newLocationData.value,
      zone_id: productData.value.zone_id
    }
    
    const response = await api.createLocation(locationPayload)
    
    // Recargar ubicaciones de la zona
    await loadZoneLocations()
    
    // Seleccionar la nueva ubicación
    productData.value.location_id = response.data.id
    
    // Limpiar y cerrar modal
    newLocationData.value = {
      code: '',
      description: '',
      location_type: 'storage',
      aisle: '',
      rack: '',
      level: '',
      position: '',
      weight: 1,
      capacity: 1,
      zone_id: null
    }
    showNewLocationDialog.value = false
    
    store.dispatch('showSuccess', 'Ubicación creada exitosamente')
  } catch (error) {
    console.error('Error creating location:', error)
    store.dispatch('showError', 'Error al crear la ubicación')
  } finally {
    creatingLocation.value = false
  }
}

const save = async () => {
  try {
    const { valid } = await form.value.validate()
    if (!valid) return

    saving.value = true
    if (isNewVersion.value) {
      await saveAsNewVersion()
      return
    }
    emit('save', productData.value)
  } catch (error) {
    console.error('Error saving product:', error)
  } finally {
    saving.value = false
  }
}

const saveAsNewVersion = async () => {
  const barcode = (productData.value.barcode || '').trim()
  try {
    const result = await store.dispatch('products/addProductBarcode', {
      productId: versionOfProductId.value,
      barcode
    })
    await store.dispatch('products/fetchProducts')
    const product = store.getters['products/productById'](versionOfProductId.value) || result.product
    store.dispatch('showSuccess', result.message || 'Código de barras agregado al producto')
    emit('version-added', product)
    emit('update:modelValue', false)
  } catch (error) {
    console.error('Error adding product barcode:', error)
    const data = error.response?.data || {}
    store.dispatch('showError', data.message || data.error || 'Error al agregar el código de barras')
  }
}

const close = () => {
  emit('update:modelValue', false)
}

// Cargar configuración de ubicaciones
const loadLocationConfig = async () => {
  try {
    // En una implementación real, esto vendría de una API o store
    // Por ahora, utilizamos la configuración por defecto
    locationConfig.value = {
      volumetricCapacity: {
        enabled: false // Se puede cambiar desde la configuración de instalaciones
      }
    }
  } catch (error) {
    console.error('Error loading location config:', error)
  }
}

// Cargar datos iniciales
onMounted(async () => {
  await Promise.all([
    store.dispatch('warehouses/fetchWarehouses'),
    store.dispatch('categories/fetchCategories'),
    loadLocationConfig()
  ])
})
</script> 